import { StatisticsActions } from '../actions/index';
import { call, all, put, takeLeading, takeEvery } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { LeagueAssetsPayload, LeagueStatsPayload, Riot } from '../types/league-of-legends';
import { Action } from 'typescript-fsa';
import { LoLHelper } from '../utils';

export async function fetchLeagueOfLegendsStats(url: string): Promise<Riot.League.RawDataAndAssets | null> {
  const res = await fetch(url);

  if (!res.ok) {
    switch (res.status) {
      case 404:
        throw new Error('Data not found');
      default:
        throw new Error('API Error');
    }
  }

  const result: Riot.League.RawData = await res.json();
  result.info.participants.forEach((p, i) => {
    result.info.participants[i].items = [
      p.item0,
      p.item1,
      p.item2,
      p.item3,
      p.item4,
      p.item5,
      p.item6,
    ];
  });

  const gameVersion = LoLHelper.extractGameVersion(result.info.gameVersion);
  const assets = await fetchLeagueOfLegendsAssets(gameVersion);

  return {
    riotDataInfo: result.info,
    gameVersion,
    assets: assets || {
      items: [],
      perkStyles: [],
      summonerSpells: [],
      champions: [],
    },
  };
}

export async function fetchLeagueOfLegendsCurrentVersion(): Promise<string | null> {
  const res = await fetch('https://ddragon.leagueoflegends.com/api/versions.json');
  if (!res.ok) {
    throw new Error('Could not fetch version json');
  }

  const versions: string[] = await res.json();

  return versions[0];
}

export async function fetchLeagueOfLegendsAssets(gameVersion: string): Promise<Riot.League.Assets | null> {
  try {
    const res = await Promise.all([
      fetch(`https://raw.communitydragon.org/${gameVersion}/plugins/rcp-be-lol-game-data/global/default/v1/perkstyles.json`),
      fetch(`https://raw.communitydragon.org/${gameVersion}/plugins/rcp-be-lol-game-data/global/default/v1/items.json`),
      fetch(`https://raw.communitydragon.org/${gameVersion}/plugins/rcp-be-lol-game-data/global/default/v1/summoner-spells.json`),
      fetch(`https://raw.communitydragon.org/${gameVersion}/plugins/rcp-be-lol-game-data/global/default/v1/champion-summary.json`),
    ]);

    const assetsArray = await Promise.all(res.map(as => as.json()));

    const [perkStyles, items, summonerSpells, champions] = assetsArray;

    return {
      items,
      perkStyles: perkStyles.styles,
      summonerSpells,
      champions,
    };
  } catch (error) {
    throw new Error('Could not fetch version json');
  }
}

export function makeStatisticsSagas(statisticsActions: StatisticsActions) {
  const {
    fetchLeagueOfLegendsAssetsAC,
    fetchLeagueOfLegendsCurrentVersionAC,
    fetchLeagueOfLegendsStatsAC,
  } = statisticsActions;


  function* fetchLeagueOfLegendsAssetsWorker(action: Action<LeagueAssetsPayload>): SagaIterator {
    const params = action.payload;
    try {
      const result: Riot.League.Assets = yield call(
        fetchLeagueOfLegendsAssets,
        params.gameVersion,
      );
      yield put(fetchLeagueOfLegendsAssetsAC.done({ params, result }));
    } catch (error) {
      yield put(fetchLeagueOfLegendsAssetsAC.failed({ params, error }));
    }
  };

  function* fetchLeagueOfLegendsAssetsWatcher(): SagaIterator {
    yield takeEvery(fetchLeagueOfLegendsAssetsAC.started, function* (action) {
      yield call(fetchLeagueOfLegendsAssetsWorker, action);
    });
  };

  function* fetchLeagueOfLegendsStatsWorker(action: Action<LeagueStatsPayload>): SagaIterator {
    const params = action.payload;
    const { url } = params;
    try {
      const result: Riot.League.RawDataAndAssets = yield call(
        fetchLeagueOfLegendsStats,
        url,
      );
      yield put(fetchLeagueOfLegendsStatsAC.done({ params, result: result.riotDataInfo }));
      yield put(fetchLeagueOfLegendsAssetsAC.done({
        params: { gameVersion: result.gameVersion },
        result: result.assets,
      }));
    } catch (error) {
      yield put(fetchLeagueOfLegendsStatsAC.failed({ params, error }));
    }
  }

  function* fetchLeagueOfLegendsStatsWatcher(): SagaIterator {
    yield takeEvery(fetchLeagueOfLegendsStatsAC.started, function* (action) {
      yield call(fetchLeagueOfLegendsStatsWorker, action);
    });
  }

  function* fetchLeagueOfLegendsCurrentVersionWorker(action: Action<void>): SagaIterator {
    const params = action.payload;
    try {
      const result: string = yield call(fetchLeagueOfLegendsCurrentVersion);
      yield put(fetchLeagueOfLegendsCurrentVersionAC.done({ params, result }));
    } catch (error) {
      yield put(fetchLeagueOfLegendsCurrentVersionAC.failed({ params, error }));
    }
  }

  function* fetchLeagueOfLegendsCurrentVersionWatcher(): SagaIterator {
    yield takeLeading(fetchLeagueOfLegendsCurrentVersionAC.started, function* (action) {
      yield call(fetchLeagueOfLegendsCurrentVersionWorker, action);
    });
  }


  function* allStatisticsSagas() {
    yield all([
      fetchLeagueOfLegendsAssetsWatcher(),
      fetchLeagueOfLegendsStatsWatcher(),
      fetchLeagueOfLegendsCurrentVersionWatcher(),
    ]);
  };

  const statisticsSagas = {
    fetchLeagueOfLegendsAssetsWatcher,
    fetchLeagueOfLegendsStatsWatcher,
    fetchLeagueOfLegendsCurrentVersionWatcher,
  };

  return {
    allStatisticsSagas,
    statisticsSagas,
  };
}
